import React, { useState } from 'react';
import { X, Monitor, Smartphone } from 'lucide-react';
import { EmailDesign } from '../types';
import { generateHtml } from '../utils/htmlGenerator';
import { ViewToggle } from '../../../common/ViewToggle';

interface PreviewModalProps {
  design: EmailDesign;
  subject?: string;
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  design,
  subject,
  isOpen,
  onClose,
  isDarkMode
}) => {
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');

  if (!isOpen) return null;

  const html = generateHtml(design);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className={`
          inline-block align-bottom rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle
          ${viewMode === 'desktop' ? 'sm:max-w-4xl' : 'sm:max-w-sm'}
          ${isDarkMode ? 'bg-gray-800' : 'bg-white'}
        `}>
          {/* Header */}
          <div className={`
            flex items-center justify-between px-6 py-4 border-b
            ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
          `}>
            <div className="flex items-center gap-4">
              <h3 className={`
                text-lg font-semibold
                ${isDarkMode ? 'text-white' : 'text-gray-900'}
              `}>
                Email Preview
              </h3>
              {subject && (
                <p className={`
                  text-sm
                  ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                `}>
                  Subject: {subject}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ViewToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                options={[
                  { value: 'desktop', icon: Monitor, title: 'Desktop view' },
                  { value: 'mobile', icon: Smartphone, title: 'Mobile view' }
                ]}
                iconSize={16}
              />
              <button
                onClick={onClose}
                className={`
                  p-2 rounded hover:bg-opacity-80
                  ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}
                `}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Preview content */}
          <div className={`
            p-6 overflow-auto
            ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}
          `}>
            <div
              className={`
                mx-auto bg-white
                ${viewMode === 'desktop' ? 'max-w-2xl' : 'max-w-sm'}
                shadow-lg
              `}
              style={{
                maxHeight: '70vh',
                overflow: 'auto'
              }}
            >
              <iframe
                srcDoc={html}
                className="w-full border-0"
                style={{
                  height: viewMode === 'desktop' ? '600px' : '800px',
                  minHeight: '400px'
                }}
                title="Email preview"
              />
            </div>
          </div>

          {/* Footer */}
          <div className={`
            flex items-center justify-end gap-2 px-6 py-4 border-t
            ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
          `}>
            <button
              onClick={onClose}
              className={`
                px-4 py-2 rounded-md transition-colors
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
